import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { format } from 'date-fns'
import type { DailyTrend, FlagBreakdown, OverviewAnalytics } from '../components/types'

export interface ReportData {
  projectName: string
  dateRange: { from: Date; to: Date }

  stats: Omit<OverviewAnalytics, 'ratedCount'>

  dailyTrends: DailyTrend[]
  flagBreakdown: FlagBreakdown[]

  summaries: {
    overview: string
    topTopics: string[]
    topicsSummary: string
    keyInsights: string[]
    recommendations: string[]
    aiGenerated?: boolean
  }
}

// ── Color palette ─────────────────────────────────────────────────────────────
const C = {
  primary:   [55,  65, 177] as [number, number, number],  // Indigo-700
  accent:    [99, 102, 241] as [number, number, number],  // Indigo-500
  success:   [22, 163,  74] as [number, number, number],  // Green-600
  warning:   [217, 119,   6] as [number, number, number], // Amber-600
  danger:    [220,  38,  38] as [number, number, number], // Red-600
  purple:    [124,  58, 237] as [number, number, number], // Violet-600
  muted:     [107, 114, 128] as [number, number, number], // Gray-500
  dark:      [17,  24,  39] as [number, number, number],  // Gray-900
  mid:       [75,  85,  99] as [number, number, number],  // Gray-600
  light:     [243, 244, 246] as [number, number, number], // Gray-100
  white:     [255, 255, 255] as [number, number, number],
  coverBg:   [30,  41,  59] as [number, number, number],  // Slate-800
  coverAcct: [99, 102, 241] as [number, number, number],  // Indigo-500
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds === 0) return 'N/A'
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  return `${(seconds / 3600).toFixed(1)}h`
}

/**
 * Strips characters that jsPDF's built-in Helvetica (WinAnsi encoding) cannot
 * render.  Keeps printable Latin-1 (U+0020–U+00FF) and replaces or removes
 * everything else so the PDF doesn't show garbled symbols.
 */
function sanitizeForPdf(str: string): string {
  if (!str) return str

  const replacements: [RegExp, string][] = [
    [/[\u{1F44D}]/gu,  '(+)'],     // 👍
    [/[\u{1F44E}]/gu,  '(-)'],     // 👎
    [/[\u{1F4CA}]/gu,  ''],        // 📊
    [/[\u{270F}\u{FE0F}?]/gu, ''], // ✏️
    [/[\u{2B50}]/gu,   '*'],       // ⭐
    [/[\u{2726}]/gu,   '*'],       // ✦
    [/\u2713/g,        'v'],       // ✓
    [/\u2717/g,        'x'],       // ✗
    [/\u2022/g,        '-'],       // •
    [/\u2019/g,        "'"],       // '
    [/\u2018/g,        "'"],       // '
    [/\u201C/g,        '"'],       // "
    [/\u201D/g,        '"'],       // "
    [/\u2014/g,        '--'],      // —  em-dash
    [/\u2013/g,        '-'],       // –  en-dash  (latin-1 but some renderers choke)
    [/\u2026/g,        '...'],     // …
    [/\u21B3/g,        '>'],       // ↳
    [/\u2192/g,        '->'],      // →
    [/\u2190/g,        '<-'],      // ←
  ]

  let out = str
  for (const [rx, rep] of replacements) {
    out = out.replace(rx, rep)
  }

  // Drop any remaining characters outside printable WinAnsi (U+0020-U+00FF)
  // but keep newlines / tabs
  out = out.replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, '')

  return out
}

export async function generateTelerivetReport(data: ReportData): Promise<Blob> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const PW = doc.internal.pageSize.getWidth()
  const PH = doc.internal.pageSize.getHeight()
  const M = 15
  const CW = PW - M * 2
  let y = M

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const text = (
    str: string, x: number, ty: number,
    opts?: { size?: number; style?: 'normal' | 'bold' | 'italic'; color?: [number, number, number]; maxWidth?: number; align?: 'left' | 'center' | 'right' }
  ) => {
    const safe = sanitizeForPdf(str)
    const { size = 10, style = 'normal', color = C.dark, maxWidth, align = 'left' } = opts || {}
    doc.setFontSize(size)
    doc.setFont('helvetica', style)
    doc.setTextColor(...color)
    if (maxWidth) {
      const lines = doc.splitTextToSize(safe, maxWidth)
      doc.text(lines, x, ty, { align })
      return lines.length * (size * 0.42)
    }
    doc.text(safe, x, ty, { align })
    return size * 0.42
  }

  const hline = (ty: number, color: [number, number, number] = C.muted, lw = 0.3) => {
    doc.setDrawColor(...color)
    doc.setLineWidth(lw)
    doc.line(M, ty, PW - M, ty)
  }

  const needPage = (h: number) => {
    if (y + h > PH - M - 12) { doc.addPage(); y = M; return true }
    return false
  }

  const section = (title: string, color: [number, number, number] = C.primary) => {
    needPage(22)
    y += 10
    text(title, M, y, { size: 13, style: 'bold', color })
    y += 3
    hline(y, color, 0.5)
    y += 7
  }

  const pill = (label: string, value: string, x: number, py: number, w: number, h: number, accent: [number, number, number]) => {
    doc.setFillColor(...C.light)
    doc.roundedRect(x, py, w, h, 2, 2, 'F')
    doc.setFillColor(...accent)
    doc.rect(x, py, 3, h, 'F')
    text(value, x + 8, py + h * 0.42, { size: 15, style: 'bold', color: accent })
    text(label,  x + 8, py + h * 0.78, { size: 8,  style: 'normal', color: C.muted })
  }

  // ── COVER PAGE ───────────────────────────────────────────────────────────────

  // Dark header bar
  doc.setFillColor(...C.coverBg)
  doc.rect(0, 0, PW, 60, 'F')

  // Accent stripe
  doc.setFillColor(...C.accent)
  doc.rect(0, 60, PW, 3, 'F')

  // Title
  text('Analytics Report', M, 20, { size: 22, style: 'bold', color: C.white })
  text('Messaging Dashboard', M, 28, { size: 11, color: [180, 185, 210] as [number,number,number] })

  // Project + date
  const dateStr = `${format(data.dateRange.from, 'MMM d, yyyy')} - ${format(data.dateRange.to, 'MMM d, yyyy')}`
  text(data.projectName, M, 42, { size: 12, style: 'bold', color: C.white })
  text(dateStr, M, 50, { size: 10, color: [160, 165, 190] as [number,number,number] })

  // Generated line
  text(`Generated ${format(new Date(), 'MMMM d, yyyy · h:mm a')}`, PW - M, 50, { size: 8, color: [130, 135, 160] as [number,number,number], align: 'right' })
  if (data.summaries.aiGenerated) {
    text('* AI-generated narrative', PW - M, 56, { size: 8, color: C.accent, align: 'right' })
  }

  y = 75

  // Quick-stat banner (4 boxes)
  const bw = (CW - 9) / 4
  const bh = 22
  const quickStats = [
    { label: 'Total Messages', value: data.stats.totalMessages.toLocaleString(), color: C.primary },
    { label: 'Active Users',   value: data.stats.activeUsers.toLocaleString(),   color: C.success },
    { label: 'Response Rate',  value: `${data.stats.responseRate}%`,             color: C.warning },
    { label: 'Satisfaction',   value: data.stats.thumbsUpRate !== 'N/A' ? `${data.stats.thumbsUpRate}%` : '-', color: C.purple },
  ]
  quickStats.forEach((s, i) => {
    pill(s.label, s.value, M + i * (bw + 3), y, bw, bh, s.color)
  })
  y += bh + 14

  // ── EXECUTIVE SUMMARY ────────────────────────────────────────────────────────

  section('Executive Summary')
  const sumH = text(data.summaries.overview, M, y, { size: 10, maxWidth: CW, color: C.mid })
  y += sumH + 12

  // ── KEY METRICS ──────────────────────────────────────────────────────────────

  section('Key Metrics')
  const cw3 = (CW - 10) / 3
  const ch = 26
  const metrics = [
    { label: 'Total Messages',     value: data.stats.totalMessages.toLocaleString(),     color: C.primary },
    { label: 'Active Users',       value: data.stats.activeUsers.toLocaleString(),       color: C.success },
    { label: 'New Users',          value: data.stats.newUsers.toLocaleString(),           color: C.accent },
    { label: 'Response Rate',      value: `${data.stats.responseRate}%`,                 color: C.warning },
    { label: 'Median Response',    value: formatDuration(data.stats.medianResponseTime), color: C.muted },
    { label: 'Satisfaction',       value: data.stats.thumbsUpRate !== 'N/A' ? `${data.stats.thumbsUpRate}%` : '-', color: C.purple },
  ]
  metrics.forEach((m, i) => {
    const row = Math.floor(i / 3)
    const col = i % 3
    pill(m.label, m.value, M + col * (cw3 + 5), y + row * (ch + 5), cw3, ch, m.color)
  })
  y += 2 * (ch + 5) + 5

  // ── MESSAGE & USER BREAKDOWN ─────────────────────────────────────────────────

  needPage(80)
  section('Message & User Breakdown')

  autoTable(doc, {
    startY: y,
    head: [['Metric', 'Count', 'Share']],
    body: [
      ['Total Messages',  data.stats.totalMessages.toLocaleString(),  '100%'],
      ['  > Incoming',     data.stats.incomingMessages.toLocaleString(),
        data.stats.totalMessages > 0 ? `${((data.stats.incomingMessages / data.stats.totalMessages) * 100).toFixed(1)}%` : '-'],
      ['  > Outgoing',     data.stats.outgoingMessages.toLocaleString(),
        data.stats.totalMessages > 0 ? `${((data.stats.outgoingMessages / data.stats.totalMessages) * 100).toFixed(1)}%` : '-'],
      ['Total Contacts',  data.stats.totalContacts.toLocaleString(),  '-'],
      ['  > Active Users', data.stats.activeUsers.toLocaleString(),
        data.stats.totalContacts > 0 ? `${((data.stats.activeUsers / data.stats.totalContacts) * 100).toFixed(1)}%` : '-'],
      ['  > New Users',    data.stats.newUsers.toLocaleString(),
        data.stats.totalContacts > 0 ? `${((data.stats.newUsers / data.stats.totalContacts) * 100).toFixed(1)}%` : '-'],
      ['Bot Handled',     data.stats.botHandled.toLocaleString(),
        (data.stats.botHandled + data.stats.humanHandled) > 0
          ? `${((data.stats.botHandled / (data.stats.botHandled + data.stats.humanHandled)) * 100).toFixed(1)}%` : '-'],
      ['Human Handled',   data.stats.humanHandled.toLocaleString(),
        (data.stats.botHandled + data.stats.humanHandled) > 0
          ? `${((data.stats.humanHandled / (data.stats.botHandled + data.stats.humanHandled)) * 100).toFixed(1)}%` : '-'],
      ['Human Takeovers', data.stats.humanTakeovers.toLocaleString(),
        data.stats.activeUsers > 0
          ? `${((data.stats.humanTakeovers / data.stats.activeUsers) * 100).toFixed(1)}%` : '-'],
      ['Avg Msgs / User', data.stats.avgMessagesPerUser, '-'],
      ['Response Rate',   `${data.stats.responseRate}%`, '-'],
      ['Median Response', formatDuration(data.stats.medianResponseTime), '-'],
    ],
    theme: 'striped',
    headStyles: { fillColor: C.primary, fontSize: 9, textColor: [255, 255, 255] },
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 55 },
      1: { cellWidth: 40, halign: 'right' },
      2: { cellWidth: 35, halign: 'right' },
    },
    margin: { left: M, right: M },
  })
  y = (doc as any).lastAutoTable.finalY + 10

  // ── FEEDBACK & RATINGS ───────────────────────────────────────────────────────

  needPage(55)
  section('User Feedback & Ratings')

  autoTable(doc, {
    startY: y,
    head: [['Feedback Type', 'Count']],
    body: [
      ['(+) Thumbs Up',           data.stats.thumbsUp.toLocaleString()],
      ['(-) Thumbs Down',         data.stats.thumbsDown.toLocaleString()],
      ['Corrected Responses',     (data.stats.correctedResponses ?? 0).toLocaleString()],
      ['Satisfaction Rate',       `${data.stats.thumbsUpRate !== 'N/A' ? data.stats.thumbsUpRate + '%' : '-'}`],
    ],
    theme: 'grid',
    headStyles: { fillColor: C.success, fontSize: 9, textColor: [255, 255, 255] },
    bodyStyles: { fontSize: 9 },
    margin: { left: M, right: M },
  })
  y = (doc as any).lastAutoTable.finalY + 10

  // ── DAILY ACTIVITY TRENDS ────────────────────────────────────────────────────

  if (data.dailyTrends.length > 0) {
    needPage(80)
    section('Daily Activity Trends')

    // Sparkline-style bar chart (text-based)
    const last14 = data.dailyTrends.slice(-14)
    const maxTotal = Math.max(...last14.map(d => d.incoming + d.outgoing), 1)
    const chartH = 30
    const barW = Math.min(CW / last14.length - 1, 10)

    last14.forEach((d, i) => {
      const total = d.incoming + d.outgoing
      const barHeight = (total / maxTotal) * chartH
      const bx = M + i * (barW + 1)
      const by = y + chartH - barHeight

      // Incoming bar
      const inH = (d.incoming / maxTotal) * chartH
      doc.setFillColor(...C.primary)
      doc.rect(bx, y + chartH - inH, barW * 0.55, inH, 'F')

      // Outgoing bar (stacked is complex; overlay beside)
      const outH = (d.outgoing / maxTotal) * chartH
      doc.setFillColor(...C.success)
      doc.rect(bx + barW * 0.55, y + chartH - outH, barW * 0.45, outH, 'F')
    })

    // Date axis labels
    y += chartH + 2
    last14.forEach((d, i) => {
      if (i % 3 === 0) {
        const bx = M + i * (barW + 1)
        text(d.date.slice(5), bx, y + 4, { size: 6, color: C.muted })
      }
    })

    // Legend
    y += 8
    doc.setFillColor(...C.primary); doc.rect(M, y, 4, 3, 'F')
    text('Incoming', M + 6, y + 3, { size: 7, color: C.muted })
    doc.setFillColor(...C.success); doc.rect(M + 30, y, 4, 3, 'F')
    text('Outgoing', M + 36, y + 3, { size: 7, color: C.muted })
    y += 10

    // Full data table
    const trendRows = last14.map(d => [
      d.date,
      d.incoming.toString(),
      d.outgoing.toString(),
      (d.incoming + d.outgoing).toString(),
      d.activeUsers.toString(),
      d.newUsers.toString(),
    ])
    autoTable(doc, {
      startY: y,
      head: [['Date', 'In', 'Out', 'Total', 'Active', 'New']],
      body: trendRows,
      theme: 'striped',
      headStyles: { fillColor: C.accent, fontSize: 8, textColor: [255, 255, 255] },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 22, halign: 'right' },
        2: { cellWidth: 22, halign: 'right' },
        3: { cellWidth: 22, halign: 'right' },
        4: { cellWidth: 28, halign: 'right' },
        5: { cellWidth: 22, halign: 'right' },
      },
      margin: { left: M, right: M },
    })
    y = (doc as any).lastAutoTable.finalY + 10
  }

  // ── ESCALATIONS & FLAGS ──────────────────────────────────────────────────────

  if (data.flagBreakdown.length > 0) {
    needPage(70)
    section('Escalations & Flags', C.danger)

    const totalActive   = data.flagBreakdown.reduce((s, f) => s + f.active, 0)
    const totalResolved = data.flagBreakdown.reduce((s, f) => s + f.resolved, 0)

    // Summary pills
    const epills = [
      { label: 'Active Flags',    value: totalActive.toString(),   color: C.danger },
      { label: 'Resolved',        value: totalResolved.toString(), color: C.success },
      { label: 'Total Escalated', value: (totalActive + totalResolved).toString(), color: C.warning },
    ]
    const ew = (CW - 10) / 3
    epills.forEach((ep, i) => {
      pill(ep.label, ep.value, M + i * (ew + 5), y, ew, 20, ep.color)
    })
    y += 28

    autoTable(doc, {
      startY: y,
      head: [['Flag Type', 'Active', 'Resolved', 'Total', 'Resolution Rate']],
      body: data.flagBreakdown.map(f => [
        sanitizeForPdf(f.name),
        f.active.toString(),
        f.resolved.toString(),
        (f.active + f.resolved).toString(),
        f.active + f.resolved > 0
          ? `${((f.resolved / (f.active + f.resolved)) * 100).toFixed(0)}%`
          : '-',
      ]),
      theme: 'grid',
      headStyles: { fillColor: C.warning, fontSize: 9, textColor: [255, 255, 255] },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { cellWidth: 22, halign: 'right' },
        2: { cellWidth: 22, halign: 'right' },
        3: { cellWidth: 22, halign: 'right' },
        4: { cellWidth: 32, halign: 'right' },
      },
      margin: { left: M, right: M },
    })
    y = (doc as any).lastAutoTable.finalY + 10
  }

  // ── KEY INSIGHTS ─────────────────────────────────────────────────────────────

  if (data.summaries.keyInsights.length > 0) {
    needPage(50)
    section('Key Insights')

    data.summaries.keyInsights.forEach(insight => {
      needPage(14)
      doc.setFillColor(...C.primary)
      doc.roundedRect(M, y - 3, 3.5, 3.5, 0.5, 0.5, 'F')
      const h = text(insight, M + 8, y, { size: 10, maxWidth: CW - 10, color: C.mid })
      y += h + 5
    })
    y += 4
  }

  // ── TOP TOPICS ───────────────────────────────────────────────────────────────

  if (data.summaries.topTopics.length > 0) {
    needPage(60)
    section('Top Conversation Topics')

    data.summaries.topTopics.forEach((topic, i) => {
      needPage(11)
      doc.setFillColor(...C.accent)
      doc.roundedRect(M, y - 3.5, 7, 5.5, 1, 1, 'F')
      text(`${i + 1}`, M + 3.5, y + 0.5, { size: 7.5, style: 'bold', color: C.white, align: 'center' })
      text(topic, M + 11, y, { size: 10, maxWidth: CW - 14, color: C.mid })
      y += 9
    })
    y += 4
  }

  // ── CONVERSATION TOPICS OVERVIEW ─────────────────────────────────────────────

  if (data.summaries.topicsSummary) {
    needPage(50)
    section('Conversation Topics Overview')
    const topicH = text(data.summaries.topicsSummary, M, y, { size: 10, maxWidth: CW, color: C.mid })
    y += topicH + 10
  }

  // ── RECOMMENDATIONS ──────────────────────────────────────────────────────────

  if (data.summaries.recommendations.length > 0) {
    needPage(60)
    section('Recommendations')

    data.summaries.recommendations.forEach((rec, i) => {
      needPage(22)
      const rh = 18
      doc.setFillColor(255, 251, 235)
      doc.roundedRect(M, y, CW, rh, 2, 2, 'F')
      doc.setFillColor(...C.warning)
      doc.rect(M, y, 3, rh, 'F')
      text(`${i + 1}.`, M + 7, y + 7, { size: 10, style: 'bold', color: C.warning })
      const recH = text(rec, M + 16, y + 7, { size: 9, maxWidth: CW - 22, color: C.mid })
      y += Math.max(rh, recH + 10) + 4
    })
  }

  // ── FOOTER ON ALL PAGES ──────────────────────────────────────────────────────

  const total = doc.getNumberOfPages()
  for (let i = 1; i <= total; i++) {
    doc.setPage(i)
    hline(PH - 12, C.muted, 0.3)
    doc.setFontSize(8)
    doc.setTextColor(...C.muted)
    doc.setFont('helvetica', 'normal')
    doc.text(sanitizeForPdf(`${data.projectName} - Analytics Report`), M, PH - 7)
    doc.text(`Page ${i} of ${total}`, PW - M, PH - 7, { align: 'right' })
    if (data.summaries.aiGenerated && i === 1) {
      doc.text('* Executive summary, insights & recommendations generated by AI', PW / 2, PH - 7, { align: 'center' })
    }
  }

  return doc.output('blob')
}

export function downloadPDF(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
